import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Assignment as CaseIcon,
  AssignmentInd as AssignedIcon,
  LocalShipping as DeliveredIcon,
  Receipt as InvoiceIcon,
  Pending as PendingIcon,
  Comment as CommentIcon,
  Notifications as NotificationsIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { notificationService, Notification } from '../services/notificationService';
import { firmAPI, dashboardAPI } from '../services/api';
import { caseService, Case } from '../services/caseService';
import { CircularProgress } from '@mui/material';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalFirms: 0,
    openCases: 0,
    assignedCases: 0,
    deliveredCases: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [clientWallets, setClientWallets] = useState<Array<{
    firmId: number;
    firmName: string;
    balance: string;
    currency: string;
  }>>([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [assignedCases, setAssignedCases] = useState<Case[]>([]);
  const [assignedCasesLoading, setAssignedCasesLoading] = useState(true);

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  useEffect(() => {
    if (!user) return;
    loadNotifications();
    loadAssignedCases();
    if (isPmpAdminOrSiteAdmin()) {
      loadDashboardStats();
    } else {
      loadClientWallets();
    }
  }, [user]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const [notificationsRes, countRes] = await Promise.all([
        notificationService.getNotifications(), // all types: case_comment, pdf_page_count, etc.
        notificationService.getUnreadCount(),
      ]);
      setNotifications(notificationsRes.data.data || []);
      setUnreadCount(countRes.data.count || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.read_at) {
      try {
        await notificationService.markAsRead(notification.id);
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
    
    // Navigate to the comment
    if (notification.action_url) {
      // Extract URL path from action_url (it might be a full URL)
      const url = new URL(notification.action_url, window.location.origin);
      navigate(url.pathname + url.hash);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      setStatsLoading(true);
      const res = await dashboardAPI.getStats();
      const data = res.data.data || {};

      setStats({
        totalFirms: data.total_firms ?? 0,
        openCases: data.open_cases ?? 0,
        assignedCases: data.assigned_cases ?? 0,
        deliveredCases: data.delivered_cases ?? 0,
      });
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadAssignedCases = async () => {
    try {
      setAssignedCasesLoading(true);
      const response = await caseService.getAssignedToMeCases({ per_page: 10 });
      setAssignedCases(response.data?.data || []);
    } catch (error) {
      console.error('Failed to load assigned cases:', error);
      setAssignedCases([]);
    } finally {
      setAssignedCasesLoading(false);
    }
  };

  const isCaseOverdue = (caseItem: any) => {
    if (!caseItem.due_date) return false;
    const activeStatuses = ['open', 'wip', 'pending', 'awaiting_info', 'quality_check'];
    if (!activeStatuses.includes(String(caseItem.status || '').toLowerCase())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(caseItem.due_date) < today;
  };

  const getStatusChip = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return <Chip label="Open" color="primary" size="small" />;
      case 'completed':
        return <Chip label="Completed" color="success" size="small" />;
      case 'on_hold':
        return <Chip label="On Hold" color="secondary" size="small" />;
      case 'closed':
        return <Chip label="Closed" color="default" size="small" />;
      case 'awaiting_info':
        return <Chip label="Awaiting Info" color="info" size="small" />;
      case 'delivered':
        return <Chip label="Delivered" color="success" size="small" />;
      case 'mrr_delivered':
        return <Chip label="MRR Delivered" color="success" size="small" />;
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" />;
      case 'wip':
        return <Chip label="WIP" color="warning" size="small" />;
      default:
        return <Chip label={status.replace(/_/g, ' ')} size="small" />;
    }
  };

  const loadClientWallets = async () => {
    try {
      setWalletsLoading(true);
      const res = await firmAPI.getAll({ type: 'client', status: 'approved', per_page: 100 });
      const firms = res.data.data?.data || res.data.data || [];
      const clientFirms = Array.isArray(firms) ? firms : [];

      const summaries = await Promise.all(
        clientFirms.map(async (firm: { id: number; name: string; type?: string }) => {
          try {
            const walletRes = await firmAPI.getWalletBalance(firm.id);
            const wallet = walletRes.data.data || walletRes.data;
            return {
              firmId: firm.id,
              firmName: firm.name,
              balance: String(wallet.balance ?? '0'),
              currency: wallet.currency ?? 'USD',
            };
          } catch {
            return null;
          }
        })
      );

      setClientWallets(summaries.filter(Boolean) as Array<{
        firmId: number;
        firmName: string;
        balance: string;
        currency: string;
      }>);
    } catch (error) {
      console.error('Failed to load client wallets:', error);
      setClientWallets([]);
    } finally {
      setWalletsLoading(false);
    }
  };

  const statsCards = [
    {
      title: 'Total Firms',
      value: statsLoading ? '...' : stats.totalFirms.toString(),
      icon: <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      color: '#1976d2',
    },
    {
      title: 'Open Cases',
      value: statsLoading ? '...' : stats.openCases.toString(),
      icon: <CaseIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      color: '#2e7d32',
    },
    {
      title: 'Assigned Cases',
      value: statsLoading ? '...' : stats.assignedCases.toString(),
      icon: <AssignedIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      color: '#dc004e',
    },
    {
      title: 'Delivered Cases',
      value: statsLoading ? '...' : stats.deliveredCases.toString(),
      icon: <DeliveredIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      color: '#ed6c02',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 2, sm: 4 }, display: { xs: 'none', sm: 'block' } }}>
        Welcome back, {user?.name}! Here's an overview of your PMP platform.
      </Typography>

      {/* Stats Cards - Only for PMP Admin */}
      {isPmpAdminOrSiteAdmin() && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
          {statsCards.map((stat, index) => (
            <Card key={index}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {stat.icon}
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="h4" component="div" sx={{ color: stat.color }}>
                      {statsLoading ? (
                        <CircularProgress size={24} sx={{ color: stat.color }} />
                      ) : (
                        stat.value
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {!assignedCasesLoading && assignedCases.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignedIcon color="secondary" />
              Assigned to Me
            </Typography>
            <Button variant="outlined" size="small" onClick={() => navigate('/cases')}>
              View All Cases
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Case ID</TableCell>
                  <TableCell>Case Title</TableCell>
                  <TableCell>Firm</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>File Number</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignedCases.map((caseItem) => {
                  const firmId = caseItem.project?.firm_id || caseItem.project?.firm?.id;
                  return (
                    <TableRow key={caseItem.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          #{caseItem.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => {
                            if (firmId) {
                              navigate(`/firms/${firmId}/cases/${caseItem.id}`);
                            } else {
                              navigate(`/cases/${caseItem.id}`);
                            }
                          }}
                        >
                          {caseItem.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {caseItem.project?.firm?.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {caseItem.project?.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(caseItem.status)}</TableCell>
                      <TableCell>
                        {caseItem.due_date ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {new Date(caseItem.due_date).toLocaleDateString()}
                            </Typography>
                            {isCaseOverdue(caseItem) && (
                              <Chip label="Overdue" color="error" size="small" />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {caseItem.file_number || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {!isPmpAdminOrSiteAdmin() && (walletsLoading || clientWallets.length > 0) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <WalletIcon color="primary" />
            <Typography variant="h6">Client Firm Funds</Typography>
          </Box>
          {walletsLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              {clientWallets.map((wallet) => (
                <Card
                  key={wallet.firmId}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/firms/${wallet.firmId}/wallet`)}
                >
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {wallet.firmName}
                    </Typography>
                    <Typography variant="h5" color="primary.main">
                      {wallet.currency} {parseFloat(wallet.balance).toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      View wallet details
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* PMP Admin Quick Actions */}
      {isPmpAdminOrSiteAdmin() && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: { xs: 1.5, sm: 2 } }}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/pending-estimates')}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PendingIcon sx={{ fontSize: 30, color: 'warning.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">Pending Estimates</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Review vendor estimates
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/vendor-invoices')}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InvoiceIcon sx={{ fontSize: 30, color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">Vendor Invoices</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage vendor billing
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/firms')}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ fontSize: 30, color: 'success.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">Manage Firms</Typography>
                    <Typography variant="body2" color="text.secondary">
                      View all firms
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Paper>
      )}

      {/* Main Content - Different layout for PMP Admin vs Other Users */}
      {isPmpAdminOrSiteAdmin() ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon color="primary" />
                <Typography variant="h6">
                  Unread Notifications
                </Typography>
                {unreadCount > 0 && (
                  <Chip 
                    label={unreadCount} 
                    color="error" 
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
              {unreadCount > 0 && (
                <Button size="small" onClick={handleMarkAllAsRead}>
                  Mark all as read
                </Button>
              )}
            </Box>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            ) : notifications.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No unread notifications.
              </Typography>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {notifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem disablePadding>
                      <ListItemButton 
                        onClick={() => handleNotificationClick(notification)}
                        sx={{ 
                          borderRadius: 1,
                          mb: 0.5,
                          bgcolor: notification.read_at ? 'transparent' : 'action.hover',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                          <CommentIcon 
                            sx={{ 
                              mr: 2, 
                              mt: 0.5,
                              color: notification.read_at ? 'text.secondary' : 'primary.main' 
                            }} 
                          />
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: notification.read_at ? 'normal' : 'bold',
                                    flex: 1 
                                  }}
                                >
                                  {notification.title}
                                </Typography>
                                {!notification.read_at && (
                                  <Chip 
                                    label="New" 
                                    color="primary" 
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                {notification.message && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {notification.message}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {new Date(notification.created_at).toLocaleString()}
                                </Typography>
                              </Box>
                            }
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                    {index < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create new case, project, or manage firms.
            </Typography>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          {/* Manage Firms Card for Non-Admin Users */}
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/firms')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">Manage Firms</Typography>
                  <Typography variant="body2" color="text.secondary">
                    View and manage your firms
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Unread Notifications for Non-PMP Admin Users */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon color="primary" />
                <Typography variant="h6">
                  Unread Notifications
                </Typography>
                {unreadCount > 0 && (
                  <Chip 
                    label={unreadCount} 
                    color="error" 
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
              {unreadCount > 0 && (
                <Button size="small" onClick={handleMarkAllAsRead}>
                  Mark all as read
                </Button>
              )}
            </Box>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            ) : notifications.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No unread notifications.
              </Typography>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {notifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem disablePadding>
                      <ListItemButton 
                        onClick={() => handleNotificationClick(notification)}
                        sx={{ 
                          borderRadius: 1,
                          mb: 0.5,
                          bgcolor: notification.read_at ? 'transparent' : 'action.hover',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                          <CommentIcon 
                            sx={{ 
                              mr: 2, 
                              mt: 0.5,
                              color: notification.read_at ? 'text.secondary' : 'primary.main' 
                            }} 
                          />
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: notification.read_at ? 'normal' : 'bold',
                                    flex: 1 
                                  }}
                                >
                                  {notification.title}
                                </Typography>
                                {!notification.read_at && (
                                  <Chip 
                                    label="New" 
                                    color="primary" 
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                {notification.message && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {notification.message}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {new Date(notification.created_at).toLocaleString()}
                                </Typography>
                              </Box>
                            }
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                    {index < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard; 